import React, { useEffect, useRef, useState } from 'react';
import './BlogGraphics.css';

// Reveal-on-scroll wrapper so graphics animate in as the reader reaches them.
function useInView(threshold = 0.25) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setSeen(true); io.disconnect(); } },
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, seen];
}

// Mac-style window chrome with the real workspace tab bar.
function Window({ activeTab, children }) {
  const tabs = [
    { k: 'Plan', i: '☰' },
    { k: 'Execute', i: '▶' },
    { k: 'Habits', i: '↻' },
    { k: 'Library', i: '◆' },
    { k: 'Journal', i: '✦' },
  ];
  return (
    <div className="bg-win">
      <div className="bg-win-bar">
        <span className="bg-win-dot" /><span className="bg-win-dot" /><span className="bg-win-dot" />
        <div className="bg-win-brand">DMYOR</div>
      </div>
      {activeTab && (
        <div className="bg-win-tabs">
          {tabs.map(t => (
            <span key={t.k} className={`bg-win-tab ${t.k === activeTab ? 'is-active' : ''}`}>
              <span className="bg-win-tab-i">{t.i}</span>{t.k}
            </span>
          ))}
        </div>
      )}
      <div className="bg-win-body">{children}</div>
    </div>
  );
}

const STATUS = {
  standby: { c: '#3b82f6', label: 'Standby' },
  active:  { c: '#f17501', label: 'Active'  },
  done:    { c: '#10b981', label: 'Done'    },
};

/* ═══════════ 1. Pillar map: the whole workspace at a glance ═══════════ */
export function PillarMapGraphic() {
  const flow = [
    { k: 'Planning', i: '☰', d: 'Break the vision into a tree' },
    { k: 'Execute', i: '▶', d: 'Do what matters today' },
    { k: 'Habits', i: '↻', d: 'Repeat the daily reps' },
  ];
  const support = [
    { k: 'Library', i: '◆', d: 'Docs + notes on every node' },
    { k: 'Journal', i: '✦', d: 'The story as it happens' },
  ];
  return (
    <Window activeTab={null}>
      <div className="bg-pm">
        <div className="bg-pm-vision">
          <span className="bg-pm-vision-tag">VISION</span>
          Earn $2,000 a month freelancing by 2027
        </div>
        <div className="bg-pm-stem" />
        <div className="bg-pm-flow">
          {flow.map((p, i) => (
            <React.Fragment key={p.k}>
              <div className="bg-pm-card bg-pm-card--main">
                <span className="bg-pm-ico">{p.i}</span>
                <span className="bg-pm-k">{p.k}</span>
                <span className="bg-pm-d">{p.d}</span>
              </div>
              {i < flow.length - 1 && <span className="bg-pm-arrow">→</span>}
            </React.Fragment>
          ))}
        </div>
        <div className="bg-pm-support-label">Underneath all three</div>
        <div className="bg-pm-support">
          {support.map(p => (
            <div key={p.k} className="bg-pm-card bg-pm-card--sub">
              <span className="bg-pm-ico">{p.i}</span>
              <div className="bg-pm-sub-text">
                <span className="bg-pm-k">{p.k}</span>
                <span className="bg-pm-d">{p.d}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Window>
  );
}

/* ═══════════ 2. Planner: real milestone cards + connectors ═══════════ */
function MilestoneCard({ type, title, status, done, total, i }) {
  const s = STATUS[status];
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div className={`bg-mscard bg-mscard--${status}`} style={{ '--i': i }}>
      <div className="bg-mscard-eyebrow">
        <span>{type}</span>
        <span className="bg-mscard-dot" style={{ background: s.c, boxShadow: `0 0 7px ${s.c}66` }} />
      </div>
      <p className="bg-mscard-title">{title}</p>
      {total > 0 && (
        <div className="bg-mscard-prog">
          <div className="bg-mscard-track"><div className="bg-mscard-fill" style={{ width: `${pct}%`, background: s.c }} /></div>
          <span className="bg-mscard-count">{done}/{total}</span>
        </div>
      )}
      <div className="bg-mscard-footer">
        <span className="bg-mscard-pill" style={{ color: s.c, borderColor: `${s.c}55`, background: `${s.c}14` }}>{s.label}</span>
        <span className="bg-mscard-actions">
          <span className="bg-ic">☆</span>
          <span className="bg-ic">✎</span>
        </span>
      </div>
    </div>
  );
}

export function PlannerTreeGraphic() {
  const [ref, seen] = useInView();
  return (
    <div ref={ref}>
      <Window activeTab="Plan">
        <div className={`bg-planner ${seen ? 'is-in' : ''}`}>
          <div className="bg-planner-vision" style={{ '--i': 0 }}>
            <span className="bg-planner-vision-tag">VISION</span>
            <span className="bg-planner-vision-text">Earn $2,000 a month freelancing by 2027</span>
          </div>

          <div className="bg-planner-railwrap">
            <span className="bg-planner-rail-label" style={{ '--i': 1 }}>MILESTONES ON THE PATH <em>3</em></span>
            <div className="bg-connector" aria-hidden="true">
              <span className="bg-connector-trunk" />
              <span className="bg-connector-bridge" />
              <span className="bg-connector-drop" style={{ left: '16.6%' }} />
              <span className="bg-connector-drop" style={{ left: '50%' }} />
              <span className="bg-connector-drop" style={{ left: '83.3%' }} />
            </div>
            <div className="bg-planner-row">
              <MilestoneCard type="Milestone" title="Build a portfolio site" status="done" done={5} total={5} i={2} />
              <MilestoneCard type="Milestone" title="Land my first 3 paying clients" status="active" done={3} total={6} i={3} />
              <MilestoneCard type="Milestone" title="Reach $2,000 in one month" status="standby" done={0} total={4} i={4} />
            </div>
          </div>
        </div>
      </Window>
    </div>
  );
}

/* ═══════════ 3. Execute: today cockpit ═══════════ */
export function ExecuteListGraphic() {
  const [ref, seen] = useInView();
  const focus = [
    { t: 'Send a proposal to the bakery owner', crumb: 'First 3 clients › Outreach', done: false },
  ];
  const todos = [
    { t: 'Set my rates: $40/hour', crumb: 'Pricing', done: true,  star: true },
    { t: 'Post 1 project on Instagram', crumb: 'Get seen', done: false, star: false },
    { t: "Follow up with last week's leads", crumb: 'First 3 clients', done: false, star: false },
    { t: 'Write my client contract', crumb: 'Admin', done: false, star: false, stale: true },
  ];
  const Row = (it, i, isFocus) => (
    <div key={it.t} className={`bg-todo ${it.done ? 'is-done' : ''} ${isFocus ? 'is-focus' : ''}`} style={{ '--i': i }}>
      <span className={`bg-todo-check ${it.done ? 'is-on' : ''}`} />
      <div className="bg-todo-body">
        <span className="bg-todo-crumb">{it.crumb}</span>
        <span className="bg-todo-text">{it.t}</span>
      </div>
      {it.stale && <span className="bg-todo-stale">stale</span>}
      <span className={`bg-todo-star ${it.star || isFocus ? 'is-on' : ''}`}>★</span>
    </div>
  );
  return (
    <div ref={ref}>
      <Window activeTab="Execute">
        <div className={`bg-exec ${seen ? 'is-in' : ''}`}>
          <div className="bg-exec-head">
            <div>
              <div className="bg-exec-date">Friday, June 6</div>
              <span className="bg-exec-vision">✦ Earn $2,000 a month freelancing</span>
            </div>
            <div className="bg-exec-ring">
              <svg viewBox="0 0 40 40" width="46" height="46">
                <circle cx="20" cy="20" r="16" fill="none" stroke="#eee" strokeWidth="4" />
                <circle cx="20" cy="20" r="16" fill="none" stroke="#10b981" strokeWidth="4"
                  strokeDasharray="100.5" strokeDashoffset={seen ? 75 : 100.5}
                  strokeLinecap="round" transform="rotate(-90 20 20)" className="bg-exec-ring-fill" />
              </svg>
              <span className="bg-exec-ring-label">1/4<br /><em>today</em></span>
            </div>
          </div>

          <div className="bg-exec-focus">
            <div className="bg-exec-focus-head">
              <span className="bg-exec-focus-label">TODAY'S FOCUS</span>
              <span className="bg-exec-slots"><i className="is-on" /><i className="is-on" /><i /></span>
            </div>
            {focus.map((it, i) => Row(it, i, true))}
          </div>

          <div className="bg-exec-list">{todos.map((it, i) => Row(it, i + 1, false))}</div>
        </div>
      </Window>
    </div>
  );
}

/* ═══════════ 4. Habits: real tracker table ═══════════ */
export function HabitsGridGraphic() {
  const [ref, seen] = useInView();
  const wd = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  const habits = [
    { t: 'Reach out to 3 people', sched: 'Mon–Fri', days: [1, 1, 1, 1, 1, 0, 0], streak: 12, rate: 86 },
    { t: 'Skill practice 1 hour', sched: 'Daily', days: [1, 1, 0, 1, 1, 0, 0], streak: 6, rate: 71 },
    { t: 'Track income', sched: 'Daily', days: [1, 1, 1, 0, 0, 0, 0], streak: 3, rate: 64 },
  ];
  return (
    <div ref={ref}>
      <Window activeTab="Habits">
        <div className={`bg-htable ${seen ? 'is-in' : ''}`}>
          <div className="bg-htable-head">
            <span className="bg-hcol-name">Habit</span>
            <span className="bg-hcol-sched">Schedule</span>
            <span className="bg-hcol-week">{wd.map((d, i) => <em key={i}>{d}</em>)}</span>
            <span className="bg-hcol-streak">Streak</span>
            <span className="bg-hcol-rate">30d</span>
          </div>
          {habits.map((h, hi) => (
            <div key={h.t} className="bg-htable-row">
              <span className="bg-hcol-name">
                <span className="bg-htick" /> {h.t}
              </span>
              <span className="bg-hcol-sched">{h.sched}</span>
              <span className="bg-hcol-week">
                {h.days.map((d, di) => (
                  <em key={di} className={`bg-hcell ${d ? 'is-done' : ''}`} style={{ '--i': hi * 7 + di }} />
                ))}
              </span>
              <span className="bg-hcol-streak">🔥 {h.streak}</span>
              <span className="bg-hcol-rate">{h.rate}%</span>
            </div>
          ))}
        </div>
      </Window>
    </div>
  );
}

/* ═══════════ 5. Library: real doc cards ═══════════ */
export function LibraryGraphic() {
  const docs = [
    { t: 'Rates and packages', time: '2d ago', snip: 'Logo $300. Brand kit $900. Hourly $40 for small fixes. Raise after 5 clients…', links: ['Pricing', 'First 3 clients'] },
    { t: 'Outreach message templates', time: '5d ago', snip: 'Short, specific, one idea for their business. Never send a generic pitch…', links: ['Outreach'] },
    { t: 'Client contract checklist', time: '1w ago', snip: '50% upfront. Two rounds of revisions. Clear deadline and who owns the files…', links: ['Admin'] },
  ];
  return (
    <Window activeTab="Library">
      <div className="bg-lib">
        <div className="bg-lib-sub">Every document for this vision. One doc can serve many nodes.</div>
        <div className="bg-lib-grid">
          {docs.map(d => (
            <div key={d.t} className="bg-lib-card">
              <div className="bg-lib-card-head">
                <span className="bg-lib-card-ico">◆</span>
                <span className="bg-lib-card-title">{d.t}</span>
                <span className="bg-lib-card-time">{d.time}</span>
              </div>
              <p className="bg-lib-card-snip">{d.snip}</p>
              <div className="bg-lib-card-links">
                {d.links.map(l => <span key={l} className="bg-lib-chip">↳ {l}</span>)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Window>
  );
}

/* ═══════════ 6. Journal: real document page ═══════════ */
export function JournalGraphic() {
  const entries = [
    { d: 'Jun 6', h: 'First paid client!', t: '$300 for a logo. Small, but someone paid me for my work. Still smiling.' },
    { d: 'Jun 4', h: 'Ghosted again', t: 'Two leads went quiet. Felt like quitting. Sent five new messages instead.' },
  ];
  return (
    <Window activeTab="Journal">
      <div className="bg-journal">
        <div className="bg-journal-tagline">A journal of how you are making your vision real, for future you to see.</div>
        <div className="bg-journal-doc">
          {entries.map(e => (
            <div key={e.d} className="bg-journal-entry">
              <div className="bg-journal-meta">
                <span className="bg-journal-day">{e.d}</span>
              </div>
              <div className="bg-journal-content">
                <h4 className="bg-journal-h">{e.h}</h4>
                <p className="bg-journal-t">{e.t}</p>
              </div>
            </div>
          ))}
          <span className="bg-journal-caret" />
        </div>
      </div>
    </Window>
  );
}

const GRAPHICS = {
  pillarmap: PillarMapGraphic,
  planner: PlannerTreeGraphic,
  execute: ExecuteListGraphic,
  habits: HabitsGridGraphic,
  library: LibraryGraphic,
  journal: JournalGraphic,
};

export default function BlogGraphic({ name }) {
  const Cmp = GRAPHICS[name];
  if (!Cmp) return null;
  return <div className="bg-wrap"><Cmp /></div>;
}
